// The process something happened in, and the step on that process's map: one picker for issues and improvements.
// Processes come from the process catalog; steps from the process's map, numbered as on the map. With `allowNew`, a
// process that isn't in the catalog yet can be added by name.
import { useEffect, useMemo, useState } from "react";
import { useLoad } from "../hooks";
import { SUITE } from "../suite";
import { imp, type MapStep } from "./impApi";

export type ProcessValue = { process: string; step: string; newName?: string; newArea?: string };
const AREAS = SUITE.map((d) => ({ id: d.id, name: d.name }));
export const NEW = "__new__";

export function ProcessPicker({ value, onChange, required, allowNew, hint, idPrefix = "pp", area }: {
  value: ProcessValue; onChange: (v: ProcessValue) => void; required?: boolean; allowNew?: boolean; hint?: string; idPrefix?: string; area?: string | null;
}) {
  const cat = useLoad(() => imp.processes().catch(() => null));
  const [steps, setSteps] = useState<MapStep[] | null>(null);
  const procs = cat.data?.processes ?? [];
  // A department's processes first.
  const sorted = useMemo(() => [...procs].sort((a, b) => Number(b.area === area) - Number(a.area === area) || a.name.localeCompare(b.name)), [procs, area]);
  const picked = procs.find((p) => p.number === value.process);
  useEffect(() => {
    let live = true;
    setSteps(null);
    if (value.process && value.process !== NEW) imp.steps(value.process).then((r) => live && setSteps(r.steps)).catch(() => live && setSteps([]));
    return () => { live = false; };
  }, [value.process]);
  const set = (p: Partial<ProcessValue>) => onChange({ ...value, ...p });
  return (
    <>
      <div className="field">
        <label htmlFor={`${idPrefix}-p`}>Process{required ? "" : <span className="muted" style={{ fontWeight: 400 }}> (optional)</span>}</label>
        <select id={`${idPrefix}-p`} className="select" required={required} value={value.process} onChange={(e) => onChange({ process: e.target.value, step: "" })}>
          <option value="">{required ? "Choose the process…" : "Not sure yet"}</option>
          {sorted.map((p) => <option key={p.number} value={p.number}>{p.number} · {p.name}{p.areaName ? ` · ${p.areaName}` : ""}</option>)}
          {allowNew && <option value={NEW}>A process that isn't in the catalog yet…</option>}
        </select>
        {hint && !value.process && <span className="hint">{hint}</span>}
        {picked && <span className="hint">{[picked.areaName, picked.minutesEach !== null ? `${picked.minutesEach} minutes each` : null, picked.map ? `Map ${picked.map.docNo}` : "No process map yet"].filter(Boolean).join(" · ")}</span>}
      </div>
      {value.process === NEW && (
        <div className="form-grid">
          <div className="field"><label htmlFor={`${idPrefix}-n`}>New process</label>
            <input id={`${idPrefix}-n`} className="input" required minLength={3} placeholder="e.g. Quote a custom part" value={value.newName ?? ""} onChange={(e) => set({ newName: e.target.value })} /></div>
          <div className="field"><label htmlFor={`${idPrefix}-a`}>Department</label>
            <select id={`${idPrefix}-a`} className="select" required value={value.newArea ?? area ?? ""} onChange={(e) => set({ newArea: e.target.value })}><option value="">Choose…</option>{AREAS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        </div>)}
      {value.process && value.process !== NEW && (
        <div className="field">
          <label htmlFor={`${idPrefix}-s`}>Step <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
          <select id={`${idPrefix}-s`} className="select" value={value.step} disabled={!steps || !steps.length} onChange={(e) => set({ step: e.target.value })}>
            <option value="">{steps === null ? "Loading the steps…" : steps.length ? "The whole process" : "No map, so no steps"}</option>
            {(steps ?? []).map((s) => <option key={s.id} value={s.id}>{s.n}. {s.name}{s.lane ? ` · ${s.lane}` : ""}</option>)}
          </select>
        </div>)}
    </>
  );
}

/** A new process typed into the picker goes into the catalog first; returns the PRC number to link. */
export async function settleProcess(v: ProcessValue): Promise<string | undefined> {
  if (v.process !== NEW) return v.process || undefined;
  const p = await imp.createProcess({ name: (v.newName ?? "").trim(), area: v.newArea || undefined });
  return p.number;
}

/** "PRC-0001 Disposition a nonconformance (MRB) · Step 3: Engineer reviews the NCR". */
export const processLine = (p: { number: string; name: string } | null | undefined, step: { name: string } | null | undefined, steps?: MapStep[]) => {
  if (!p) return null;
  const n = step && steps ? steps.find((s) => s.name === step.name)?.n : undefined;
  return `${p.number} ${p.name}${step ? ` · Step${n ? ` ${n}` : ""}: ${step.name}` : ""}`;
};
